import styled, { css } from 'styled-components';
import theme from '../../theme';
import { getLighterThemeColor, getDarkerThemeColor, getUserThemeContrastColor, toRgba } from '../../theme/colors';

// Base button styles shared by all variants
const baseButtonStyles = css`
  display: flex;
  align-items: center;
  justify-content: ${(props) => props.$justifyContent || 'center'};
  gap: 8px;
  border-radius: ${(props) => (props.$square ? '0px' : '22px')};
  font-family: inherit;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid transparent;
  text-decoration: none;
  width: ${(props) => (props.$fullWidth ? '100%' : 'auto')};
  position: relative;
  overflow: hidden;
  box-sizing: border-box; /* Ensure consistent box-sizing for both a and button elements */

  &:focus {
    outline: none;
    z-index: 1;
    box-shadow: ${(props) => props.theme.shadows.BOX_SHADOW};
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    background-color: ${(props) => props.theme.colors.NEU_50};
    border-color: ${(props) => props.theme.colors.NEU_50};
    color: ${(props) => props.theme.colors.NEU_200};
  }
`;

// Size variants
const sizeStyles = {
  'x-small': css`
    padding: 4px 10px;
    min-width: 70px;
    font-size: 12px !important;
  `,
  small: css`
    padding: 6px 12px;
    min-width: 80px;
    font-size: 14px;
  `,
  medium: css`
    padding: 8px 15px;
    min-width: 120px;
    font-size: 16px;
  `,
  large: css`
    padding: 10px 20px;
    min-width: 150px;
    font-size: 22px !important;
  `,
};

// Specific styling for icon-only buttons
const iconOnlyStyles = css`
  min-width: unset;
  padding: ${({ $size }) => ($size === 'x-small' ? '4px' : $size === 'small' ? '6px' : $size === 'large' ? '12px' : '8px')};
  width: ${({ $size }) => ($size === 'x-small' ? '26px' : $size === 'small' ? '32px' : $size === 'large' ? '48px' : '40px')};
  height: ${({ $size }) => ($size === 'x-small' ? '26px' : $size === 'small' ? '32px' : $size === 'large' ? '48px' : '40px')};
  border-radius: ${(props) => (props.$square ? '0px' : '50%')};
  aspect-ratio: 1;
  box-sizing: border-box; /* Explicitly set box-sizing for icon-only buttons */
`;

// Button variants
const variantStyles = {
  //solid dark to solid light (Default primary button, secondary, inverted theme)
  primary: css`
    background-color: ${(props) => (props.$themeColor ? getDarkerThemeColor(props.$themeColor) : props.theme.colors.PRIMARY)};
    color: ${(props) => props.theme.colors.NEU_0};

    &:hover:not(:disabled) {
      background-color: ${(props) => (props.$themeColor ? getLighterThemeColor(props.$themeColor) : props.theme.colors.PRIMARY_LIGHT)};
      color: ${(props) => (props.$themeColor ? getDarkerThemeColor(props.$themeColor) : props.theme.colors.NEU_PRIMARY)};
    }
  `,

  //solid to outline (primary, action)
  secondary: css`
    background-color: ${(props) => props.$bgColor || props.theme.colors.NEU_PRIMARY};
    color: ${(props) => props.theme.colors.NEU_0};

    &:hover:not(:disabled) {
      background-color: ${(props) => props.theme.colors.NEU_0};
      color: ${(props) => props.$bgColor || props.theme.colors.NEU_PRIMARY};
      border-color: ${(props) => props.$bgColor || props.theme.colors.NEU_PRIMARY};
    }
  `,

  // outline to solid dark (outline, secondary inverted)
  tertiary: css`
    background-color: ${toRgba(theme.colors.NEU_0, 0.94)};
    border-color: ${(props) => props.$bgColor || props.theme.colors.PRIMARY};
    color: ${(props) => props.$bgColor || props.theme.colors.PRIMARY};

    &:hover:not(:disabled) {
      background-color: ${(props) => props.$bgColor || props.theme.colors.PRIMARY};
      color: ${(props) => props.theme.colors.NEU_0};
    }
  `,

  // solid light to solid dark
  themed: css`
    background-color: ${(props) => (props.$themeColor ? getLighterThemeColor(props.$themeColor) : props.theme.colors.PRIMARY_LIGHT)};
    color: ${(props) => (props.$themeColor ? getDarkerThemeColor(props.$themeColor) : props.theme.colors.PRIMARY)};

    &:hover:not(:disabled) {
      background-color: ${(props) => (props.$themeColor ? getDarkerThemeColor(props.$themeColor) : props.theme.colors.PRIMARY)};
      color: ${(props) => props.theme.colors.NEU_0};
    }
  `,

  //transparent to solid dark (basic - old version)
  ghost: css`
    background-color: transparent;
    color: ${(props) => props.theme.colors.NEU_PRIMARY};

    &:hover:not(:disabled) {
      background-color: ${(props) => props.$bgColor || props.theme.colors.NEU_100};
      color: ${(props) => (props.$bgColor ? getUserThemeContrastColor(props.$bgColor) : props.theme.colors.NEU_PRIMARY)};
    }
  `,
  asText: css`
    background: none;
    border: none;
    padding: 0;
    min-width: 0;
    color: ${(props) => props.theme.colors.NEU_PRIMARY};
    text-decoration: underline;
    font-size: 14px;

    &:hover:not(:disabled) {
      color: ${(props) => props.$bgColor || props.theme.colors.PRIMARY};
      text-decoration: none;
    }

    &:focus {
      box-shadow: none;
    }
  `,
};

// Loading styles
const loadingStyles = css`
  cursor: wait;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: ${toRgba(theme.colors.NEU_0, 0.15)};
    z-index: 1;
  }
`;

// Button component with common styling
export const StyledButton = styled.button`
  ${baseButtonStyles}
  ${(props) => sizeStyles[props.$size || 'medium']}
  ${(props) => variantStyles[props.$variant || 'primary']}
  ${(props) => props.$iconOnly && iconOnlyStyles}
  ${(props) => props.$isLoading && loadingStyles}

  p {
    color: inherit;
    transition: color 0.2s ease;
    font-size: inherit;
  }
`;

// Link component that looks like a button
export const StyledLink = styled.a`
  ${baseButtonStyles}
  ${(props) => sizeStyles[props.$size || 'medium']}
  ${(props) => variantStyles[props.$variant || 'primary']}
  ${(props) => props.$iconOnly && iconOnlyStyles}

  p {
    color: inherit;
    transition: color 0.2s ease;
    font-size: inherit;
  }
`;
